import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Banknote } from 'lucide-react';
import { format } from 'date-fns';
import { useCurrency } from '../hooks/useCurrency';
import { formatApiError } from '../lib/apiError';
import { PayoutFormDialog } from '../components/payouts/PayoutFormDialog';
import { PayoutCardList } from '../components/payouts/PayoutCardList';
import { PayoutTable } from '../components/payouts/PayoutTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  worker_id: '',
  amount: '',
  date: format(new Date(), 'yyyy-MM-dd'),
  notes: ''
});

const Payouts = () => {
  const [workers, setWorkers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workerBalance, setWorkerBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const { format: fmt, symbol } = useCurrency();

  const fetchData = useCallback(async () => {
    try {
      const [workersRes, payoutsRes] = await Promise.all([
        axios.get(`${API_URL}/api/workers?active_only=true`),
        axios.get(`${API_URL}/api/payouts`)
      ]);
      setWorkers(workersRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!formData.worker_id) {
      setWorkerBalance(null);
      return;
    }
    let active = true;
    setBalanceLoading(true);
    axios.get(`${API_URL}/api/workers/${formData.worker_id}/balance`)
      .then((res) => {
        if (active) setWorkerBalance(res.data);
      })
      .catch(() => {
        if (active) setWorkerBalance(null);
      })
      .finally(() => {
        if (active) setBalanceLoading(false);
      });
    return () => { active = false; };
  }, [formData.worker_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worker_id || !formData.amount) {
      toast.error('Worker and amount are required');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    if (workerBalance && parseFloat(formData.amount) > workerBalance.net_balance) {
      toast.error(`Payout cannot exceed the worker's net balance of ${fmt(workerBalance.net_balance)}`);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/payouts`, {
        worker_id: formData.worker_id,
        amount: parseFloat(formData.amount),
        date: formData.date,
        notes: formData.notes
      });
      toast.success('Payout recorded successfully');
      setIsDialogOpen(false);
      setFormData(emptyForm());
      setWorkerBalance(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to record payout'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (payoutId) => {
    if (!window.confirm('Are you sure you want to delete this payout record?')) return;

    try {
      await axios.delete(`${API_URL}/api/payouts/${payoutId}`);
      toast.success('Payout deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payout');
    }
  };

  const totalPaidOut = payouts.reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payouts-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payouts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Payouts
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Record wage payments made to workers
          </p>
        </div>

        <PayoutFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          workers={workers}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          symbol={symbol}
          fmt={fmt}
          workerBalance={workerBalance}
          balanceLoading={balanceLoading}
        />
      </div>

      <Card className="bg-[#2E4632] text-white rounded-lg shadow-sm">
        <CardContent className="p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium uppercase tracking-wide">
                Total Paid Out (All Time)
              </p>
              <p className="text-4xl font-bold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-paid-out">
                {fmt(totalPaidOut)}
              </p>
            </div>
            <div className="p-4 bg-white/20 rounded-full">
              <Banknote className="w-8 h-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      <PayoutCardList payouts={payouts} fmt={fmt} onDelete={handleDelete} />
      <PayoutTable payouts={payouts} fmt={fmt} onDelete={handleDelete} />
    </div>
  );
};

export default Payouts;
